import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Edit,
    Building2,
    DollarSign,
    MapPin,
    Calendar,
    Target,
    TrendingUp,
    Clock,
    FileText,
    Image as ImageIcon,
    CheckCircle,
    XCircle,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    X,
    Users,
    Trophy,
    Award
} from 'lucide-react';
import { Project } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { API_BASE_URL } from '../../config/api';

const ViewProjectDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get project data from navigation state (passed from list)
    const project = location.state?.project as Project | undefined;

    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);

    const formatCurrency = (amount: number, symbol: string | null = null) => {
        const currencySymbol = symbol || '$';
        return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleImageClick = (index: number) => {
        setSelectedImageIndex(index);
        setShowImageModal(true);
    };

    const handleNextImage = () => {
        if (project?.images && selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex + 1) % project.images.length);
        }
    };

    const handlePrevImage = () => {
        if (project?.images && selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex - 1 + project.images.length) % project.images.length);
        }
    };

    const handleCloseModal = () => {
        setShowImageModal(false);
        setSelectedImageIndex(null);
    };

    // Keyboard navigation
    useEffect(() => {
        if (!showImageModal || !project?.images) return;

        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            } else if (e.key === 'ArrowLeft' && selectedImageIndex !== null) {
                setSelectedImageIndex((selectedImageIndex - 1 + project.images.length) % project.images.length);
            } else if (e.key === 'ArrowRight' && selectedImageIndex !== null) {
                setSelectedImageIndex((selectedImageIndex + 1) % project.images.length);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showImageModal, selectedImageIndex, project?.images]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</Badge>;
            case 'funded':
                return <Badge variant="default" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Funded</Badge>;
            case 'draft':
                return <Badge variant="default" className="flex items-center gap-1"><Clock className="w-3 h-3" />Draft</Badge>;
            case 'closed':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Closed</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    const getImageUrl = (imageUrl: string | null | undefined) => {
        if (!imageUrl) return null;
        const APP_URL = API_BASE_URL;
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            return imageUrl;
        }
        if (imageUrl.startsWith('/uploads')) {
            return `${APP_URL}${imageUrl}`;
        }
        return `${APP_URL}/uploads/${imageUrl}`;
    };

    const progressPercentage = project?.funding_goal && project?.amount_raised
        ? (project.amount_raised / project.funding_goal) * 100
        : 0;

    // If no project data is passed via state, show error and redirect
    if (!project) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Project data not found. Please navigate to this page from the projects list.</p>
                    <button
                        onClick={() => navigate('/admin/projects')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Funded Success Banner */}
            {project.amount_raised >= project.funding_goal && (
                <div className="bg-green-600 text-white px-6 py-3 flex items-center justify-center gap-3 shadow-md animate-pulse-subtle">
                    <Trophy className="w-5 h-5" />
                    <span className="font-bold tracking-wide uppercase text-sm sm:text-base">This Project is Fully Funded!</span>
                    <Trophy className="w-5 h-5" />
                </div>
            )}
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/projects')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">View Project Details</h1>
                                <p className="text-gray-600">Project information and details</p>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate('/admin/update-project', { state: { project } })}
                            variant="primary"
                        >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Project
                        </Button>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Building2 className="w-5 h-5 mr-2" />
                                Basic Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-700">Project Title</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">{project.title}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <div className="mt-1">{getStatusBadge(project.status)}</div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Category</label>
                                <p className="mt-1 text-sm text-gray-900 capitalize">{project.category?.name || 'N/A'}</p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-700">Short Description</label>
                                <p className="mt-1 text-sm text-gray-900">{project.short_description || 'N/A'}</p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-700">Full Description</label>
                                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{project.full_description || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Location</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{project.location || 'N/A'}</p>
                                </div>
                            </div>
                            {project.latitude && project.longitude && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Coordinates</label>
                                    <p className="mt-1 text-sm text-gray-900">{project.latitude}, {project.longitude}</p>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium text-gray-700">Created At</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{formatDate(project.created_at)}</p>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Updated At</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{formatDate(project.updated_at)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Funding Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <DollarSign className="w-5 h-5 mr-2" />
                                Funding Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Funding Goal</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {formatCurrency(project.funding_goal, project.currency_symbol)}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Amount Raised</label>
                                <p className="mt-1 text-lg font-semibold text-green-600">
                                    {formatCurrency(project.amount_raised || 0, project.currency_symbol)}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Progress</label>
                                <div className="mt-2">
                                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                                        <div
                                            className={`h-3 rounded-full transition-all duration-1000 ${project.amount_raised >= project.funding_goal ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}
                                            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex items-center justify-between mt-1.5">
                                        <p className={`text-sm font-bold ${project.amount_raised >= project.funding_goal ? 'text-green-600' : 'text-gray-600'}`}>
                                            {progressPercentage.toFixed(1)}% {project.amount_raised >= project.funding_goal ? 'Target Achieved' : 'Raised'}
                                        </p>
                                        {project.amount_raised >= project.funding_goal && (
                                            <Award className="w-4 h-4 text-green-600" />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Remaining Amount</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {formatCurrency(project.funding_goal - (project.amount_raised || 0), project.currency_symbol)}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Is Funded</label>
                                <p className="mt-1">
                                    {project.amount_raised >= project.funding_goal ? (
                                        <Badge variant="success" className="bg-green-600 text-white border-none px-3 py-1 animate-bounce-subtle">
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            Fully Funded
                                        </Badge>
                                    ) : (
                                        <Badge variant="default" className="bg-gray-100 text-gray-600 border-gray-200">Not Funded</Badge>
                                    )}
                                </p>
                            </div>
                             {(project.start_date || project.created_at) && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Start Date</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(project.start_date || project.created_at)}</p>
                                    </div>
                                </div>
                            )}
                            {project.deadline && (
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Deadline</label>
                                    <div className="mt-1 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{formatDate(project.deadline)}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ROI Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <TrendingUp className="w-5 h-5 mr-2" />
                                ROI Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Expected ROI Percentage</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">{project.expected_roi_percentage}%</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">ROI Duration (Months)</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-gray-400" />
                                    <p className="text-lg font-semibold text-gray-900">{project.roi_duration_months} months</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    {project.images && project.images.length > 0 && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images ({project.images.length})
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {project.images.map((image, index) => {
                                        const imageUrl = getImageUrl(image.image_url);
                                        return (
                                            <div
                                                key={image.project_images_id}
                                                className="relative group cursor-pointer"
                                                onClick={() => handleImageClick(index)}
                                            >
                                                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent group-hover:border-blue-500 transition-all">
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt={`${project.title} - Image ${index + 1}`}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <ImageIcon className="w-8 h-8 text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                                                    <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="mt-4 text-sm text-gray-500 text-center">Click on any image to view in full size</p>
                            </div>
                        </div>
                    )}

                    {/* Documents */}
                    {project.documents && project.documents.length > 0 && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <FileText className="w-5 h-5 mr-2" />
                                    Documents ({project.documents.length})
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {project.documents.map((doc) => {
                                        const docUrl = getImageUrl(doc.document_url);
                                        return (
                                            <div
                                                key={doc.project_documents_id}
                                                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                <FileText className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-gray-900 font-medium text-sm">{doc.document_name}</p>
                                                    {docUrl && (
                                                        <a
                                                            href={docUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-xs text-blue-600 hover:text-blue-800 mt-1 inline-block"
                                                        >
                                                            View Document
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {showImageModal && selectedImageIndex !== null && project?.images && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
                    <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Previous Button */}
                        {project.images.length > 1 && (
                            <button
                                onClick={handlePrevImage}
                                className="absolute left-4 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                            >
                                <ChevronLeft className="w-8 h-8" />
                            </button>
                        )}

                        {/* Image */}
                        <div className="relative max-w-full max-h-full flex items-center justify-center">
                            {(() => {
                                const imageUrl = getImageUrl(project.images[selectedImageIndex].image_url);
                                return imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={`${project.title} - Image ${selectedImageIndex + 1}`}
                                        className="max-w-full max-h-[90vh] object-contain rounded-lg"
                                    />
                                ) : (
                                    <div className="text-white">Image not available</div>
                                );
                            })()}
                        </div>

                        {/* Next Button */}
                        {project.images.length > 1 && (
                            <button
                                onClick={handleNextImage}
                                className="absolute right-4 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                            >
                                <ChevronRight className="w-8 h-8" />
                            </button>
                        )}

                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-black bg-opacity-50 rounded-full text-white text-sm">
                            {selectedImageIndex + 1} / {project.images.length}
                        </div>

                        {/* Thumbnail Strip */}
                        {project.images.length > 1 && (
                            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4">
                                {project.images.map((image, index) => {
                                    const thumbUrl = getImageUrl(image.image_url);
                                    return (
                                        <button
                                            key={image.project_images_id}
                                            onClick={() => setSelectedImageIndex(index)}
                                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                                index === selectedImageIndex
                                                    ? 'border-blue-500 ring-2 ring-blue-300'
                                                    : 'border-transparent opacity-60 hover:opacity-100'
                                            }`}
                                        >
                                            {thumbUrl ? (
                                                <img
                                                    src={thumbUrl}
                                                    alt={`Thumbnail ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewProjectDetails;

